import { ArrowLeft, FileText, Phone } from 'lucide-react';
import { Link, useParams } from 'react-router';

import { ClientPage } from '@/components/client/ClientPage';
import { ErrorState } from '@/components/ui/empty-state';
import { StatusBadge } from '@/components/ui/status-badge';
import { INVOICE_TONES, useClientInvoice } from '@/hooks/invoices';
import { formatDay } from '@/lib/dates';
import { formatCents } from '@shared/money';

/*
 * /portal/invoices/:id — ROADMAP.md §12.5.
 *
 * **Remit-to instructions, not a pay button** — there is no online payment,
 * deliberately (business-rules.md §7), so the most useful thing this page can
 * do is say exactly how to pay and who to ask. The PDF link serves the same
 * document the email carried.
 */

export function ClientInvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const invoice = useClientInvoice(id);

  if (invoice.isPending) {
    return (
      <ClientPage title="Invoice">
        <div className="bg-cream-100 h-64 animate-pulse rounded-lg" />
      </ClientPage>
    );
  }

  if (invoice.error || invoice.data === undefined) {
    return (
      <ClientPage title="Invoice">
        <ErrorState
          title="This invoice could not be loaded."
          onRetry={() => {
            void invoice.refetch();
          }}
        />
      </ClientPage>
    );
  }

  const detail = invoice.data;

  return (
    <ClientPage
      title={detail.invoiceNumber}
      description={
        detail.issueDate !== null
          ? `Issued ${formatDay(detail.issueDate)} · Due ${
              detail.dueDate === detail.issueDate
                ? 'on receipt'
                : detail.dueDate !== null
                  ? formatDay(detail.dueDate)
                  : 'on receipt'
            }`
          : undefined
      }
    >
      <Link
        to="/portal/invoices"
        className="mb-4 inline-flex min-h-touch items-center gap-1 text-body text-bark-700 underline underline-offset-2"
      >
        <ArrowLeft aria-hidden="true" className="size-4" />
        All invoices
      </Link>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <StatusBadge label={detail.statusLabel} tone={INVOICE_TONES[detail.status]} />
        {detail.isOverdue ? (
          <span className="rounded-sm bg-red-100 px-1.5 py-0.5 text-meta font-medium text-red-800">
            Overdue
          </span>
        ) : null}
        <a
          href={`/api/client/invoices/${detail.id}/pdf`}
          target="_blank"
          rel="noreferrer"
          className="ml-auto inline-flex min-h-touch items-center gap-1 text-body text-bark-700 underline underline-offset-2"
        >
          <FileText aria-hidden="true" className="size-4" />
          Download the PDF
        </a>
      </div>

      {detail.status === 'void' ? (
        <div className="bg-cream-100 mb-4 rounded-lg border border-ink-300 p-4 text-body text-ink-900">
          This invoice has been voided and does not need paying.
          {detail.replacedByInvoiceNumber !== null
            ? ` It was replaced by ${detail.replacedByInvoiceNumber}.`
            : ''}
        </div>
      ) : null}

      <section
        aria-label="What this invoice covers"
        className="rounded-lg border border-ink-300 bg-white"
      >
        <ul className="divide-y divide-ink-300">
          {detail.lineItems.map((line) => (
            <li key={line.id} className="flex items-start justify-between gap-3 p-4">
              <div>
                <p className="text-body text-ink-900">{line.description}</p>
                <p className="mt-0.5 text-meta text-ink-500">
                  {line.pricingUnit === 'hourly'
                    ? `${String(line.quantity)} hr × ${formatCents(line.unitPriceCents)}/hr`
                    : line.quantity !== 1
                      ? `${String(line.quantity)} × ${formatCents(line.unitPriceCents)}`
                      : null}
                </p>
              </div>
              <span className="whitespace-nowrap text-body font-medium tabular-nums text-ink-900">
                {formatCents(line.totalCents)}
              </span>
            </li>
          ))}
        </ul>

        <dl className="space-y-1.5 border-t border-ink-300 p-4 text-body">
          <div className="flex justify-between">
            <dt className="text-ink-500">Subtotal</dt>
            <dd className="tabular-nums text-ink-900">{formatCents(detail.subtotalCents)}</dd>
          </div>
          {detail.taxEnabled ? (
            <div className="flex justify-between">
              <dt className="text-ink-500">
                Utah sales tax ({(detail.taxRateBps / 100).toFixed(2)}%)
              </dt>
              <dd className="tabular-nums text-ink-900">{formatCents(detail.taxCents)}</dd>
            </div>
          ) : null}
          <div className="flex justify-between text-label font-semibold">
            <dt className="text-ink-900">Total</dt>
            <dd className="tabular-nums text-ink-900">{formatCents(detail.totalCents)}</dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-ink-500">Paid</dt>
            <dd className="tabular-nums text-ink-900">{formatCents(detail.amountPaidCents)}</dd>
          </div>
          <div className="flex justify-between text-label font-semibold">
            <dt className="text-ink-900">Balance</dt>
            <dd className="tabular-nums text-ink-900">{formatCents(detail.balanceCents)}</dd>
          </div>
        </dl>
      </section>

      {detail.notes !== null ? (
        <p className="mt-4 whitespace-pre-wrap rounded-lg border border-ink-300 bg-white p-4 text-body text-ink-700">
          {detail.notes}
        </p>
      ) : null}

      {detail.payments.length > 0 ? (
        <section aria-label="Payments" className="mt-6">
          <h2 className="mb-2 text-h2 text-ink-900">Payments we’ve received</h2>
          <ul className="divide-y divide-ink-300 rounded-lg border border-ink-300 bg-white">
            {detail.payments.map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-3 p-4">
                <span className="text-body text-ink-700">
                  {formatDay(row.paidOn)} · {row.methodLabel}
                  {row.reference !== null ? ` · Ref ${row.reference}` : ''}
                  {row.isRefunded ? ' · Refunded' : ''}
                </span>
                <span className="whitespace-nowrap text-body tabular-nums text-ink-900">
                  {formatCents(row.amountCents)}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {detail.status !== 'void' ? (
        <section
          aria-label="How to pay"
          className="bg-cream-100 mt-6 rounded-lg border border-ink-300 p-4"
        >
          <h2 className="text-h2 text-ink-900">How to pay</h2>
          <p className="mt-2 text-body text-ink-700">{detail.remitTo.instructions}</p>
          <p className="mt-3 flex items-center gap-2 text-body text-ink-900">
            <Phone aria-hidden="true" className="size-4" />
            {detail.remitTo.businessName} · {detail.remitTo.phone}
          </p>
        </section>
      ) : null}
    </ClientPage>
  );
}
